import {
  ORNAMENT_GROUPS,
  ornamentsInGroup,
  type OrnamentGroup,
  type OrnamentKey,
} from "@/lib/label-canvas/ornaments"
import { PALETTE_HEX, luminance, paletteColour } from "@/lib/label-canvas/palette"
import { OrnamentGlyph } from "./OrnamentGlyph"

// The ornament menu (canvas v2): the ten groups as chips, then a grid of
// previews drawn in the colour a new ornament will get. Tapping one adds it
// to the label (one at a time). Desktop shows it in a dialog from the rail;
// the phone shows it as the "Ornaments" half of the Decor sheet.

/** Each preview sits on a scrap of label paper that the ornament colour reads on. */
function paperFor(color: string) {
  return luminance(color) > 0.3 ? PALETTE_HEX.black : PALETTE_HEX["warm-white"]
}

export function OrnamentMenu({
  group,
  onGroup,
  color,
  onAdd,
}: {
  group: OrnamentGroup
  onGroup: (group: OrnamentGroup) => void
  /** The colour a new ornament gets (`ld.ornamentColor`). */
  color: string
  onAdd: (key: OrnamentKey) => void
}) {
  const colourName = paletteColour(color)?.label ?? "the current colour"
  const active = ORNAMENT_GROUPS.find((g) => g.key === group) ?? ORNAMENT_GROUPS[0]
  const paper = paperFor(color)
  return (
    <div className="studio-ornaments">
      <div className="studio-chips studio-chips-scroll" role="group" aria-label="Ornament groups">
        {ORNAMENT_GROUPS.map((g) => (
          <button
            key={g.key}
            type="button"
            className="studio-chip"
            aria-pressed={g.key === active.key}
            onClick={() => onGroup(g.key)}
          >
            {g.label}
          </button>
        ))}
      </div>

      <div className="studio-ornament-grid" role="group" aria-label={active.label}>
        {ornamentsInGroup(active.key).map((o) => (
          <button key={o.key} type="button" className="studio-ornament-tile" onClick={() => onAdd(o.key)}>
            <span className="studio-ornament-paper" style={{ backgroundColor: paper }}>
              <OrnamentGlyph ornament={o.key} color={color} />
            </span>
            <span className="studio-ornament-name">{o.label}</span>
          </button>
        ))}
      </div>

      <div className="field-hint studio-ornaments-note">
        New ornaments arrive in {colourName.toLowerCase()}. Choose one on the label to recolour or resize it.
      </div>
    </div>
  )
}
